import {
  Button,
  PickList,
  Skeleton,
  formatRelative,
  toast,
  type PickListItem,
} from '@westhetics/ui'
import { useMemo } from 'react'
import { LoadError } from '@/app/load-error'
import { useCatalogOptions, type CatalogProcedureOption } from '@/features/catalog/api'
import { errorMessage, fieldErrors } from '@/lib/errors'
import { useCreateCatalogRequest, useOwnCatalogRequests } from './api'

/**
 * A escolha na lista mestre da Westhetics (RN-ADM-70, RN-ADM-72). A lista é fechada: quem não acha
 * o procedimento pede a inclusão dali mesmo (RN-ADM-75), e o estado dos pedidos fica à vista.
 */
export function CatalogPicker({
  value,
  onChange,
  multiple,
  disableInCatalog = false,
  autoFocus,
}: {
  value: string[]
  onChange: (ids: string[], options: CatalogProcedureOption[]) => void
  multiple: boolean
  /** Na escolha em lote, o que a clínica já tem como avulso não pode ser escolhido de novo. */
  disableInCatalog?: boolean
  autoFocus?: boolean
}) {
  const options = useCatalogOptions()
  const requests = useOwnCatalogRequests()
  const createRequest = useCreateCatalogRequest()

  const items = useMemo<PickListItem[]>(
    () =>
      (options.data ?? []).map((option) => {
        const taken = disableInCatalog && option.isInClinicCatalog
        return {
          id: option.id,
          label: option.name,
          group: option.groupName,
          keywords: option.aliases,
          description: option.description ?? undefined,
          disabled: taken,
          note: taken
            ? 'já no seu catálogo'
            : option.hasConsentForm
              ? undefined
              : 'sem termo publicado',
        }
      }),
    [options.data, disableInCatalog],
  )

  if (options.isError) {
    return (
      <LoadError
        title="Não conseguimos carregar a lista da Westhetics"
        error={options.error}
        onRetry={() => void options.refetch()}
      />
    )
  }
  if (options.isPending) {
    return (
      <div role="status" aria-label="Carregando" className="flex flex-col gap-2">
        <Skeleton className="h-[40px] w-full" />
        <Skeleton className="h-[200px] w-full" />
      </div>
    )
  }

  const openRequests = (requests.data ?? []).filter((request) => request.status !== 'added')

  return (
    <div className="flex flex-col gap-3">
      <PickList
        label="Procedimentos da lista da Westhetics"
        items={items}
        value={value}
        multiple={multiple}
        autoFocus={autoFocus}
        searchPlaceholder="Buscar na lista da Westhetics"
        onChange={(ids) =>
          onChange(
            ids,
            ids
              .map((id) => options.data.find((option) => option.id === id))
              .filter((option): option is CatalogProcedureOption => Boolean(option)),
          )
        }
        emptySearch={(query) => {
          // O nome do pedido é a busca; a API aceita de 2 a 80 caracteres.
          const requestName = query.trim()
          const canRequest = requestName.length >= 2 && requestName.length <= 80
          return (
            <div className="flex flex-col items-start gap-3">
              <span>
                Não encontramos "{query}" na lista da Westhetics. A lista é fechada porque cada
                procedimento dela precisa de um termo de consentimento revisado.
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={!canRequest}
                loading={createRequest.isPending}
                loadingLabel="Enviando o pedido…"
                onClick={() =>
                  createRequest.mutate(
                    { name: requestName, note: null },
                    {
                      onSuccess: () =>
                        toast(
                          `Pedido de "${requestName}" enviado — a Westhetics avisa quando incluir`,
                        ),
                      // A causa de um 422 vem no campo; a frase geral não diz o que corrigir.
                      onError: (error) => toast(fieldErrors(error).name ?? errorMessage(error)),
                    },
                  )
                }
              >
                {canRequest
                  ? `Pedir inclusão de "${requestName}"`
                  : 'O nome do pedido tem de 2 a 80 caracteres'}
              </Button>
            </div>
          )
        }}
      />
      {openRequests.length > 0 ? (
        <ul className="font-body text-secondary text-[12px] leading-[1.8] font-light">
          {openRequests.map((request) => (
            <li key={request.id}>
              {request.name} · pedido {formatRelative(new Date(request.createdAt))} ·{' '}
              {request.status === 'open'
                ? 'aguardando a Westhetics'
                : `recusado: ${request.resolutionNote ?? ''}`}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
